import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Controller, FieldError, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import * as yup from 'yup';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Alert from '@mui/material/Alert';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppSelector } from '../../app/hooks';
import { useGetProviderInAccountsQuery } from '../../app/services/site';
import Loading from '../../common/components/Loading';
import RoundedContainer from '../../common/components/RoundedContainer';
import TagList from '../../common/components/TagList';
import {
  getAccountInterviewTypes,
  getAutoIntakePacket,
  getEhrEnabled,
} from '../../common/utils/accountUtils';
import { ReportType } from '../../common/utils/assessmentType';
import checkNullData from '../../common/utils/checkNullData';
import { countryType, sortedCountryCodes, USCountryCode } from '../../common/utils/countries';
import { populateLanguage } from '../../common/utils/languages';
import { Role } from '../../common/utils/loginRole';
import { getSortedUsers } from '../../common/utils/patientUtils';
import { isAccountStudyAdmin, isClinicAdmin, isSuperAdmin } from '../../common/utils/permissionUtils';
import { getStateErrorKey, usStates } from '../../common/utils/usStates';
import { validateEmail } from '../../common/utils/validationUtils';
import { CoCMAssessmentTypeItem, InterviewTypeItem } from '../../types/AccountType';
import { BillingSetting, BillingSettingsResponse } from '../../types/BillingType';
import { ClinicAccountItems } from '../../types/ClinicAccountType';
import { SiteInfo } from '../../types/ClinicSiteType';
import { ResponseError } from '../../types/ResponseError';
import { Provider } from '../../types/UserTypes';
import { selectUser } from '../auth/authSlice';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" color="secondary" />;

const getOptionLabel = (
  id: any,
  interviewTypes: Record<number, InterviewTypeItem | CoCMAssessmentTypeItem>
) => {
  const option = interviewTypes[id];
  return `${option?.name} (${populateLanguage(option?.language)})`;
};

interface SiteFormProps {
  siteId?: number;
  data?: SiteInfo;
  clinicAccounts?: ClinicAccountItems[];
  billingSettings?: BillingSettingsResponse;
  onFormSubmit: (data: SiteInfo, siteId?: number, successCallback?: () => void) => Promise<void>;
  triggerRequestCoCMAssessmentTypes?: any;
  coCMAssessmentTypes?: Record<string, CoCMAssessmentTypeItem>;
  isLoadingCoCMAssessmentType?: boolean;
}

const defaultFormValues: SiteInfo = {
  clinicAccountId: '',
  name: '',
  location: '',
  country: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  timezone: '',
  externalId: '',
  locationId: '',
  suicideEmails: [],
  interviewResultEmails: [],
  intakePacketInterviews: [],
  intakePacketDefaultProvider: undefined,
  optInPatientReminders: false,
  optInCoCM: false,
  screeningAssessments: [],
  diagnosticAssessments: [],
  defaultCareManagerId: undefined,
};

const StyleTextField = styled(TextField)(({ theme }) => ({
  [`&.MuiFormControl-root`]: {
    width: '100%',

    [theme.breakpoints.up('md')]: {
      width: '49%',
    },
  },
}));

const StyleAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '49%',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  '&.MuiCheckbox-root': {
    width: '45px',
    height: '45px',
  },
  '&.Mui-checked': {
    color: theme.palette.secondary.main,
  },
  '&.MuiCheckbox-indeterminate': {
    color: theme.palette.secondary.main,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 30,
  },
}));

export default function AddOrEditForm({
  siteId,
  data,
  clinicAccounts,
  billingSettings,
  onFormSubmit,
  triggerRequestCoCMAssessmentTypes,
  coCMAssessmentTypes,
  isLoadingCoCMAssessmentType,
}: SiteFormProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));

  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [stateLabel, setStateLabel] = useState<string>(t('clinicSite.manageSites.siteForm.state'));
  const [isUSA, setIsUSA] = useState<boolean>(false);
  const [countryCode, setCountryCode] = useState<string>('');
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [timezones, setTimezones] = useState<string[]>([]);
  const currentUser = useAppSelector(selectUser);
  const [isStateDisabled, setStateDisabled] = useState<boolean>(false);
  const [autoIntakePacket, setAutoIntakePacket] = useState<boolean | undefined>(false);
  const [openDrop, setOpenDrop] = useState<boolean>(false);
  const [intakePacketInterviews, setIntakePacketInterviews] = useState<InterviewTypeItem[]>([]);
  const [multiCheckInterview, setMultiCheckInterview] = useState<number[]>([]);
  const [activeProviderList, setActiveProviderList] = useState<Provider[]>([]);
  const [activeCareProviderList, setActiveCareProviderList] = useState<Provider[]>([]);
  const [ehrEnabled, setEhrEnabled] = useState<boolean | undefined>(false);
  const [coCMEnabled, setCoCMEnabled] = useState<boolean>(false);
  const [stateData, setStateData] = useState<string>('');

  const noState = 'NO STATE';

  const validationSchema = yup
    .object({
      clinicAccountId: yup
        .string()
        .trim()
        .test(
          'validate-account',
          t('clinicSite.manageSites.error.blankAccount'),
          function (clinicAccountId) {
            return clinicAccountId !== '';
          }
        ),
      name: yup.string().required(t('clinicSite.manageSites.error.blankName')),
      location: yup.string().required(t('clinicSite.manageSites.error.blankLocation')),
      addressLine1: yup.string().required(t('clinicSite.manageSites.error.blankAddressLine1')),
      addressLine2: yup.string(),
      country: yup.string().required(t('clinicSite.manageSites.error.blankCountry')),
      city: yup.string().required(t('clinicSite.manageSites.error.blankCity')),
      state: yup
        .string()
        .typeError(`${t('clinicSite.manageSites.error.blankState')}`)
        .test('state', '', function (state) {
          if (!state && state !== noState) {
            return this.createError({ message: t(getStateErrorKey(stateLabel)) });
          }
          return true;
        }),
      zip: yup.string().required(t('clinicSite.manageSites.error.blankZipCode')),
      timezone: yup.string().required(t('clinicSite.manageSites.error.blankTimeZone')),
      externalId: yup.string(),
      locationId: yup.string(),
      intakePacketInterviews: yup.mixed(),
      intakePacketDefaultProvider:
        activeProviderList.length === 0
          ? yup.string()
          : yup.string().when('intakePacketInterviews', {
              is: (intakePacketInterviews: number[]) => {
                return intakePacketInterviews.length > 0;
              },
              then: yup
                .string()
                .required(t('clinicSite.manageSites.error.blankIntakePacketDefaultProvider')),
            }),
      defaultCareManagerId:
        activeCareProviderList.length === 0 || !isSuperAdmin(currentUser.roleId)
          ? yup.string()
          : yup.string().when('optInCoCM', {
              is: (optInCoCM: boolean) => optInCoCM,
              then: yup
                .string()
                .required(t('clinicSite.manageSites.error.blankDefaultCareManager')),
            }),
    })
    .required();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    resetField,
    reset,
  } = useForm<SiteInfo>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultFormValues,
  });

  const { data: providerData } = useGetProviderInAccountsQuery(
    getValues('clinicAccountId')
      ? {
          clinicAccountId: getValues('clinicAccountId'),
        }
      : skipToken
  );

  const { fields, append } = useFieldArray({
    control,
    name: 'billingSettings',
  });

  const validateEmails = useCallback((value: string[]): boolean => {
    return !value.some((email) => !validateEmail(email));
  }, []);

  useEffect(() => {
    if (providerData?.providers) {
      const sortProviders = getSortedUsers(providerData?.providers);
      setActiveProviderList(sortProviders);
      const careManagers = sortProviders.filter(
        (provider) => provider?.loginRoleId === Role.CareManager
      );
      setActiveCareProviderList(careManagers);
    }
  }, [providerData]);

  // bind controls based on selected country code
  useEffect(() => {
    if (countryCode) {
      setIsUSA(countryCode === USCountryCode);
      const { adminDistrict, timezone } = sortedCountryCodes[countryCode];
      setTimezones(Array.isArray(timezone) ? timezone : [timezone]);
      setStateLabel(adminDistrict);
      const isNoState = adminDistrict === noState;
      setStateDisabled(isNoState);

      if (isNoState) {
        setValue('state', noState);
        setStateData(noState);
      }
      setValue('country', countryCode);
      setCountryCode(countryCode);
    }
  }, [countryCode, setValue, isUSA]);

  // initial data load
  useEffect(() => {
    if (siteId && data !== undefined) {
      if (!isDataLoaded) {
        setIsDataLoaded(true);
        const bindEditControls = async () => {
          Object.keys(defaultFormValues).forEach((field: any) => {
            setValue(field, checkNullData(data[field as keyof SiteInfo]));

            if (data.optInCoCM) {
              setCoCMEnabled(data.optInCoCM);
            }

            if (data.country) {
              setStateLabel(sortedCountryCodes[data.country as keyof countryType].adminDistrict);
              setCountryCode(data.country);
            } else {
              setStateDisabled(true);
            }
            if (data.state) {
              setStateData(data.state);
            }
            if (data.intakePacketInterviews?.length !== 0 && data.intakePacketInterviews) {
              setMultiCheckInterview(data.intakePacketInterviews);
              setValue('intakePacketInterviews', data.intakePacketInterviews);
              if (data.intakePacketDefaultProvider) {
                setValue('intakePacketDefaultProvider', data.intakePacketDefaultProvider);
              }
            }
          });
        };
        bindEditControls();
      }
      if (clinicAccounts) {
        setAutoIntakePacket(getAutoIntakePacket(clinicAccounts, data.clinicAccountId));
        setIntakePacketInterviews(getAccountInterviewTypes(clinicAccounts, data.clinicAccountId));
        setEhrEnabled(getEhrEnabled(clinicAccounts, data.clinicAccountId));
      }
    }
    if (!siteId) {
      setValue('clinicAccountId', currentUser.clinicAccountId);
      setAutoIntakePacket(getAutoIntakePacket(clinicAccounts, currentUser.clinicAccountId));
      setIntakePacketInterviews(
        getAccountInterviewTypes(clinicAccounts, currentUser.clinicAccountId)
      );
      setEhrEnabled(getEhrEnabled(clinicAccounts, currentUser.clinicAccountId));
    }
    triggerRequestCoCMAssessmentTypes({
      clinicAccountId: data?.clinicAccountId || currentUser.clinicAccountId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, isDataLoaded, clinicAccounts, setValue, data, currentUser, control]);

  useEffect(() => {
    if (billingSettings && billingSettings?.billingSettings?.length > 0) {
      resetField('billingSettings');
      billingSettings.billingSettings.forEach((setting: BillingSetting) => {
        append({
          interviewCategory: setting.interviewCategory,
          firstTime: setting.firstTime,
          subsequentTimes: setting.subsequentTimes,
        });
      });
    }
  }, [append, billingSettings, resetField]);

  const handleAccountChange = (e: any, values: any) => {
    const selectedValue = values ? values.id : '';
    setValue('clinicAccountId', selectedValue);
    setAutoIntakePacket(getAutoIntakePacket(clinicAccounts, selectedValue));
    setMultiCheckInterview([]);
    setEhrEnabled(getEhrEnabled(clinicAccounts, selectedValue));
    setOpenDrop(false);
    triggerRequestCoCMAssessmentTypes({ clinicAccountId: selectedValue });

    if (getAutoIntakePacket(clinicAccounts, selectedValue)) {
      setIntakePacketInterviews(getAccountInterviewTypes(clinicAccounts, selectedValue));
      setValue('intakePacketInterviews', []);
    } else {
      resetField('intakePacketInterviews');
      resetField('intakePacketDefaultProvider');
    }
  };

  const handleCountryChange = (e: any, values: any) => {
    const selectedValue = values ? values.code : '';

    setValue('state', '');
    setStateData('');

    setValue('timezone', '');
    setCountryCode(selectedValue);
    setValue('country', selectedValue);

    if (!selectedValue) {
      setStateDisabled(true);
    }
  };

  const handleStateChange = (e: any, values: any) => {
    const selectedState = values ? values.code : '';
    setValue('state', selectedState);
    setStateData(selectedState);
    if (!selectedState) {
      setValue('timezone', '');
    }
  };

  const handleTimezoneChange = (e: any, values: any) => {
    const selectedState = values || '';
    setValue('timezone', selectedState);
  };

  const handleDefaultProviderChange = (e: any, values: any) => {
    const selectedState = values ? values.id : '';
    setValue('intakePacketDefaultProvider', selectedState);
  };

  const handleDefaultCareManagerChange = (e: any, values: any) => {
    const selectedValue = values ? values.id : undefined;
    setValue('defaultCareManagerId', selectedValue);
  };

  const filterAccountOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => getAccountOptionLabel(option),
  });
  const filterCountryOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => option.name,
  });
  const filterStateOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => option.name,
  });
  const filterOptionsTimezone = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => option,
  });
  const filterDefaultProviderOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => getDefaultProviderLabel(option),
  });
  const filterDefaultCareManagerOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => getDefaultProviderLabel(option),
  });
  const filterBillingSettingsOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => getBillingTypeLabel(option),
  });

  const getAccountOptionLabel = (option: any) => `${option.name} (${option.id})`;

  const getDefaultProviderLabel = (option: any) =>
    option.firstName && option.lastName ? `${option.lastName}, ${option.firstName}` : '';

  const getBillingTypeLabel = (option: any) => option.name;

  const handleClick = () => {
    setOpenDrop(!openDrop);
  };

  const handleChangeSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    let interviewIds: number[] = [];
    if (isChecked) {
      interviewIds = (intakePacketInterviews.length !== 0 ? intakePacketInterviews! : []).map(
        (interview) => interview.id
      );
    } else {
      resetField('intakePacketDefaultProvider');
    }
    setValue('intakePacketInterviews', interviewIds);
    setMultiCheckInterview(interviewIds);
  };

  const handleOptInPatientRemindersChange = (event: any) => {
    const isChecked = event?.target?.checked;
    setValue('optInPatientReminders', isChecked);
  };

  const handleOptInCoCM = (event: any) => {
    const isChecked = event?.target?.checked;
    setCoCMEnabled(isChecked);
    setValue('optInCoCM', isChecked);
    if (!isChecked) {
      setValue('screeningAssessments', data?.screeningAssessments || []);
      setValue('diagnosticAssessments', data?.diagnosticAssessments || []);
    }
  };

  const handleChangeCheck = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    let interviewIds: number[] = [];
    const checkExistValue = multiCheckInterview.includes(parseInt(value));
    if (!checkExistValue) {
      interviewIds = [...multiCheckInterview, parseInt(value)];
      setMultiCheckInterview(interviewIds);
    } else {
      interviewIds = [...multiCheckInterview.filter((item) => item !== parseInt(value))];
      setMultiCheckInterview(interviewIds);
    }
    setValue('intakePacketInterviews', interviewIds);
    if (interviewIds.length === 0) {
      resetField('intakePacketDefaultProvider');
    }
  };

  const onSubmit = async (data: SiteInfo) => {
    // This error is only for set error manually
    if (errors.suicideEmails) {
      setError('suicideEmails', errors.suicideEmails as any);
    }
    if (errors.interviewResultEmails) {
      setError('interviewResultEmails', errors.interviewResultEmails as any);
    }

    if (errors.suicideEmails || errors.interviewResultEmails) {
      return;
    }

    try {
      if (!autoIntakePacket && data && data.intakePacketInterviews) {
        data.intakePacketInterviews = undefined;
      }
      if (!ehrEnabled) {
        data.locationId = undefined;
      }
      await onFormSubmit(data, siteId, () => {
        if (!siteId) {
          reset();
        }
        setPageErrors([]);
      });
    } catch (e) {
      const {
        data: { error },
      } = e as ResponseError;
      setPageErrors(error);
    }
  };

  const onInvalidSubmit = () => {
    // This error is only for set error manually
    if (errors.suicideEmails) {
      setError('suicideEmails', errors.suicideEmails as any);
    }
    if (errors.interviewResultEmails) {
      setError('interviewResultEmails', errors.interviewResultEmails as any);
    }
  };

  const submitForm = async () => {
    handleSubmit(onSubmit, onInvalidSubmit)();
  };

  const children = (
    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
      {(intakePacketInterviews && intakePacketInterviews.length !== 0
        ? intakePacketInterviews
        : []
      ).map((interview: InterviewTypeItem) => {
        return (
          <FormControlLabel
            key={interview.id}
            label={`${interview.name} (${populateLanguage(interview.language)})`}
            value={interview.id}
            control={
              <StyledCheckbox
                checked={multiCheckInterview.includes(interview.id)}
                onChange={handleChangeCheck}
              />
            }
          />
        );
      })}
    </Box>
  );

  return (
    <Box
      component="form"
      noValidate
      sx={{
        px: 0,
        pt: 0,
      }}
    >
      <RoundedContainer sx={{ marginBottom: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            px: matches ? 0 : 3,
            py: 3,
          }}
        >
          {!siteId && currentUser.roleId === Role.SuperAdmin && (
            <Controller
              name="clinicAccountId"
              control={control}
              render={({ fieldState, formState, field: { value }, ...props }) => (
                <StyleAutocomplete
                  {...props}
                  data-testid="mui-component-select-clinic-account"
                  options={clinicAccounts || []}
                  getOptionLabel={getAccountOptionLabel}
                  onChange={handleAccountChange}
                  value={value ? clinicAccounts?.find((item) => item.id === value) || [] : null}
                  filterOptions={filterAccountOptions}
                  renderOption={(props, option: any) => {
                    return (
                      <li {...props} key={`clinicAccountId-${option.id}`} value={option.id}>
                        {getAccountOptionLabel(option)}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.clinicAccountId}
                      helperText={errors.clinicAccountId?.message}
                      margin="normal"
                      fullWidth
                      label={t('clinicSite.manageSites.siteForm.account')}
                    />
                  )}
                />
              )}
            />
          )}
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                data-testid="site-name-input"
                error={!!errors.name}
                helperText={errors.name?.message}
                margin="normal"
                fullWidth
                label={t('clinicSite.manageSites.siteForm.name')}
                variant="outlined"
                sx={{
                  width: siteId || currentUser.roleId !== Role.SuperAdmin ? '100%' : '49%',
                  [theme.breakpoints.down('md')]: {
                    width: '100%',
                  },
                }}
              />
            )}
          />
          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <StyleTextField
                {...field}
                error={!!errors.location}
                helperText={errors.location?.message}
                margin="normal"
                fullWidth
                label={t('clinicSite.manageSites.siteForm.location')}
                variant="outlined"
              />
            )}
          />
          <Controller
            name="country"
            control={control}
            render={({ fieldState, formState, field: { value }, ...props }) => (
              <StyleAutocomplete
                {...props}
                data-testid="mui-component-select-country"
                options={Object.values(sortedCountryCodes)}
                getOptionLabel={(option: any) => (option.name ? option.name : '')}
                onChange={handleCountryChange}
                value={value ? sortedCountryCodes[value] : null}
                filterOptions={filterCountryOptions}
                renderOption={(props, option: any) => {
                  return (
                    <li {...props} key={option.code} value={option.code}>
                      {option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!errors.country}
                    helperText={errors.country?.message}
                    margin="normal"
                    fullWidth
                    label={t('clinicSite.manageSites.siteForm.country')}
                  />
                )}
              />
            )}
          />

          <Controller
            name="addressLine1"
            control={control}
            render={({ field }) => (
              <StyleTextField
                {...field}
                error={!!errors.addressLine1}
                helperText={errors.addressLine1?.message}
                margin="normal"
                fullWidth
                label={t('clinicSite.manageSites.siteForm.addressLine1')}
                variant="outlined"
              />
            )}
          />
          <Controller
            name="addressLine2"
            control={control}
            render={({ field }) => (
              <StyleTextField
                {...field}
                error={!!errors.addressLine2}
                helperText={errors.addressLine2?.message}
                margin="normal"
                fullWidth
                label={t('clinicSite.manageSites.siteForm.addressLine2')}
                variant="outlined"
              />
            )}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: matches ? 'column' : 'row',
              justifyContent: 'space-between',
              width: matches ? '100%' : '49%',
            }}
          >
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <StyleTextField
                  {...field}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  margin="normal"
                  fullWidth
                  label={t('clinicSite.manageSites.siteForm.city')}
                  variant="outlined"
                />
              )}
            />
            <Controller
              name="state"
              control={control}
              render={({ fieldState, formState, field, ...props }) =>
                isUSA ? (
                  <StyleAutocomplete
                    {...props}
                    data-testid="mui-component-select-state"
                    options={usStates}
                    getOptionLabel={(option: any) => (option.name ? option.name : '')}
                    onChange={handleStateChange}
                    disabled={isStateDisabled || !countryCode}
                    value={
                      field.value ? usStates.find((state) => state.code === field.value) : null
                    }
                    filterOptions={filterStateOptions}
                    renderOption={(props, option: any) => {
                      return (
                        <li {...props} key={option.code} value={option.code}>
                          {option.name}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!errors.state}
                        helperText={errors.state?.message}
                        margin="normal"
                        fullWidth
                        label={stateLabel}
                      />
                    )}
                  />
                ) : (
                  <StyleTextField
                    {...field}
                    error={!!errors.state}
                    helperText={errors.state?.message}
                    margin="normal"
                    fullWidth
                    disabled={isStateDisabled || !countryCode}
                    label={stateLabel}
                    variant="outlined"
                  />
                )
              }
            />
          </Box>
          <Controller
            name="zip"
            control={control}
            render={({ field }) => (
              <StyleTextField
                {...field}
                error={!!errors.zip}
                helperText={errors.zip?.message}
                margin="normal"
                fullWidth
                label={t('clinicSite.manageSites.siteForm.zipCode')}
                variant="outlined"
              />
            )}
          />
          <Controller
            name="timezone"
            control={control}
            render={({ fieldState, formState, field: { value }, ...props }) => (
              <StyleAutocomplete
                {...props}
                data-testid="mui-component-select-timezone"
                options={timezones || []}
                disabled={isUSA && !stateData}
                getOptionLabel={(option: any) => option || null}
                onChange={handleTimezoneChange}
                value={value ? timezones?.find((timezone) => timezone === value) || null : null}
                filterOptions={filterOptionsTimezone}
                renderOption={(props, option: any) => {
                  return (
                    <li {...props} key={option} value={option}>
                      {option}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!errors.timezone}
                    helperText={errors.timezone?.message}
                    margin="normal"
                    fullWidth
                    disabled={isUSA && !stateData}
                    label={t('clinicSite.manageSites.siteForm.timezone')}
                  />
                )}
              />
            )}
          />
          <Controller
            name="externalId"
            control={control}
            render={({ field }) => (
              <StyleTextField
                {...field}
                error={!!errors.externalId}
                helperText={errors.externalId?.message}
                margin="normal"
                fullWidth
                label={t('clinicSite.manageSites.siteForm.externalId')}
                variant="outlined"
              />
            )}
          />
          <Controller
            name="locationId"
            control={control}
            render={({ field }) => (
              <StyleTextField
                {...field}
                disabled={!ehrEnabled}
                error={!!errors.locationId}
                helperText={errors.locationId?.message}
                margin="normal"
                fullWidth
                label={t('clinicSite.manageSites.siteForm.locationId')}
                variant="outlined"
              />
            )}
          />
        </Box>
      </RoundedContainer>
      <RoundedContainer sx={{ paddingTop: 1, paddingBottom: 3, marginBottom: 2 }}>
        <Typography
          sx={{ color: 'rgba(0, 0, 0, 0.5)', marginBottom: 1, paddingLeft: 3, paddingTop: 1 }}
        >
          {t('clinicSite.manageSites.siteForm.siteSettings')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            px: matches ? 0 : 3,
          }}
        >
          <Controller
            name="suicideEmails"
            control={control}
            render={({ field }) => (
              <TagList
                sx={{ width: matches ? '100%' : '49%' }}
                label={t('clinicSite.manageSites.siteForm.suicideAlertEmails')}
                tags={field.value}
                error={errors.suicideEmails}
                setTags={(tags: string[]) => {
                  setValue('suicideEmails', tags);
                }}
                validationFn={validateEmails}
                setError={() => {
                  setError('suicideEmails', {
                    message: t('clinicSite.manageSites.error.invalidEmail'),
                  });
                }}
                clearError={() => {
                  clearErrors('suicideEmails');
                }}
              />
            )}
          />
          <Controller
            name="interviewResultEmails"
            control={control}
            render={({ field }) => (
              <TagList
                sx={{ width: matches ? '100%' : '49%' }}
                label={t('clinicSite.manageSites.siteForm.interviewResultEmails')}
                tags={field.value}
                error={errors.interviewResultEmails}
                setTags={(tags: string[]) => {
                  setValue('interviewResultEmails', tags);
                }}
                validationFn={validateEmails}
                setError={() => {
                  setError('interviewResultEmails', {
                    message: t('clinicSite.manageSites.error.invalidEmail'),
                  });
                }}
                clearError={() => {
                  clearErrors('interviewResultEmails');
                }}
              />
            )}
          />
          <Controller
            name="optInPatientReminders"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                {...field}
                control={<StyledCheckbox />}
                checked={field.value}
                onChange={handleOptInPatientRemindersChange}
                label={`${t('patient.patientInfo.optInPatientReminders')}`}
              />
            )}
          />
        </Box>
      </RoundedContainer>
      <RoundedContainer sx={{ paddingTop: 1, paddingBottom: 3, marginBottom: 2 }}>
        <Typography
          sx={{ color: 'rgba(0, 0, 0, 0.5)', marginBottom: 1, paddingLeft: 3, paddingTop: 1 }}
        >
          {t('clinicSite.manageSites.siteForm.intakePacketSettings')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            px: matches ? 0 : 3,
            pt: 0,
          }}
        >
          <FormControl
            error={!!errors.intakePacketInterviews}
            sx={{ width: matches ? '100%' : '49%' }}
            disabled={!autoIntakePacket}
          >
            <Box
              sx={{
                borderRadius: 2,
                border: `1px solid ${
                  !!!errors.intakePacketInterviews ? theme.xPalette.sliver : theme.xPalette.warning
                }`,
                height: '-webkit-fill-available',
                p: '5px',
                m: '16px 0px 8px 0px',
                position: 'relative',
              }}
            >
              <ListItemButton
                onClick={handleClick}
                disabled={!autoIntakePacket}
                sx={{
                  borderRadius: '8px 8px 0px 0px',
                  bottom: '2px',
                  pb: 0,
                  px: 1,
                }}
              >
                <ListItemText
                  sx={{
                    '& .MuiTypography-root': {
                      display: 'inline-block',
                      width: '98%',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    },
                  }}
                  primary={
                    multiCheckInterview.length > 0
                      ? intakePacketInterviews
                          .filter((interview) =>
                            multiCheckInterview.some(
                              (mulInterview) =>
                                mulInterview?.toString() === interview.id?.toString()
                            )
                          )
                          .map(
                            (interview) =>
                              `${interview.name} (${populateLanguage(interview.language)})`
                          )
                          .join(', ')
                      : t('clinicSite.manageSites.siteForm.interviewsForIntakePacket')
                  }
                />
                {openDrop ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse
                in={openDrop}
                timeout="auto"
                unmountOnExit
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '52px',
                  zIndex: 99,
                  backgroundColor: 'white',
                  width: '100%',
                  border: `1px solid ${
                    !!!errors.intakePacketInterviews
                      ? theme.xPalette.sliver
                      : theme.xPalette.warning
                  }`,
                  maxHeight: '400px',
                  overflow: 'auto',
                  borderRadius: '0 0 10px 10px',
                }}
              >
                <List component="div" sx={{ m: '16px 40px' }}>
                  <Controller
                    name="intakePacketInterviews"
                    control={control}
                    render={({ field }) => (
                      <>
                        <FormControlLabel
                          {...field}
                          label={`${t('clinicSite.manageSites.siteForm.selectAllInterviews')}`}
                          control={
                            <StyledCheckbox
                              checked={intakePacketInterviews.length === multiCheckInterview.length}
                              indeterminate={
                                multiCheckInterview.length > 0 &&
                                multiCheckInterview.length !== intakePacketInterviews.length
                              }
                              onChange={handleChangeSelectAll}
                            />
                          }
                        />
                        {children}
                      </>
                    )}
                  />
                </List>
              </Collapse>
            </Box>
            {!!errors.intakePacketInterviews && (
              <FormHelperText>
                {errors.intakePacketInterviews && (errors.intakePacketInterviews as any).message}
              </FormHelperText>
            )}
          </FormControl>
          <Controller
            name="intakePacketDefaultProvider"
            control={control}
            render={({ fieldState, formState, field: { value }, ...props }) => (
              <Autocomplete
                {...props}
                fullWidth
                data-testid="mui-component-select-default-provider"
                options={activeProviderList || []}
                getOptionLabel={getDefaultProviderLabel}
                onChange={handleDefaultProviderChange}
                sx={{ width: matches ? '100%' : '49%' }}
                value={value ? activeProviderList?.find((item) => item.id === value) || null : null}
                disabled={
                  !autoIntakePacket ||
                  !getValues('intakePacketInterviews') ||
                  getValues('intakePacketInterviews')?.length === 0 ||
                  activeProviderList.length === 0
                }
                filterOptions={filterDefaultProviderOptions}
                renderOption={(props, option: any) => {
                  return (
                    <li {...props} key={option.id} value={option.id}>
                      {getDefaultProviderLabel(option)}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!errors.intakePacketDefaultProvider}
                    helperText={errors.intakePacketDefaultProvider?.message}
                    margin="normal"
                    fullWidth
                    label={t('clinicSite.manageSites.siteForm.defaultProviderForIntakePacket')}
                  />
                )}
              />
            )}
          />
        </Box>
      </RoundedContainer>
      {(isClinicAdmin(currentUser.roleId) || currentUser.roleId === Role.SuperAdmin || isAccountStudyAdmin(currentUser.roleId)) &&
        fields &&
        fields?.length > 0 && (
          <RoundedContainer sx={{ paddingTop: 1, paddingBottom: 3, marginBottom: 2 }}>
            <Typography
              sx={{ color: 'rgba(0, 0, 0, 0.5)', marginBottom: 1, paddingLeft: 3, paddingTop: 1 }}
            >
              {t('clinicSite.manageSites.siteForm.billingUsageReportSettings.title')}
            </Typography>

            {fields &&
              fields.map((item: BillingSetting, index) => {
                return (
                  <Box sx={{ paddingTop: 3 }} key={`${item.interviewCategory.name}`}>
                    <Typography fontWeight="bold" variant="body1" paddingLeft={3}>
                      {item.interviewCategory.name}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        px: matches ? 0 : 3,
                        pt: 0,
                      }}
                    >
                      <Controller
                        name={`billingSettings.${index}.firstTime`}
                        control={control}
                        render={({ fieldState, formState, field: { value }, ...props }) => (
                          <StyleAutocomplete
                            {...props}
                            sx={{ width: matches ? '100%' : '49%' }}
                            data-testid={`mui-component-billingSettings.${index}.firstTime`}
                            options={billingSettings?.billingTypes ?? []}
                            getOptionLabel={getBillingTypeLabel}
                            filterOptions={filterBillingSettingsOptions}
                            value={
                              value
                                ? billingSettings?.billingTypes?.find((item) => item.id === value)
                                : null
                            }
                            onChange={(_, values: any) => {
                              if (values) {
                                const selectedValue = values.id;
                                setValue(`billingSettings.${index}.firstTime`, selectedValue);
                              }
                            }}
                            renderOption={(props, option: any) => {
                              return (
                                <li {...props} key={`billing-type-${option.id}`} value={option.id}>
                                  {option.name}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!errors.billingSettings?.[index]?.firstTime}
                                helperText={errors.billingSettings?.[index]?.firstTime?.message}
                                margin="normal"
                                fullWidth
                                InputProps={{
                                  ...params.InputProps,
                                }}
                                label={t(
                                  'clinicSite.manageSites.siteForm.billingUsageReportSettings.firstTime'
                                )}
                              />
                            )}
                          />
                        )}
                      />
                      <Controller
                        name={`billingSettings.${index}.subsequentTimes`}
                        control={control}
                        render={({ fieldState, formState, field: { value }, ...props }) => (
                          <StyleAutocomplete
                            {...props}
                            sx={{ width: matches ? '100%' : '49%' }}
                            data-testid={`mui-component-billingSettings.${index}.subsequentTimes`}
                            options={billingSettings?.billingTypes ?? []}
                            getOptionLabel={getBillingTypeLabel}
                            filterOptions={filterBillingSettingsOptions}
                            value={
                              value
                                ? billingSettings?.billingTypes?.find((item) => item.id === value)
                                : null
                            }
                            onChange={(_, values: any) => {
                              if (values) {
                                const selectedValue = values.id;
                                setValue(`billingSettings.${index}.subsequentTimes`, selectedValue);
                              }
                            }}
                            renderOption={(props, option: any) => {
                              return (
                                <li {...props} key={option.id} value={option.id}>
                                  {option.name}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!errors.billingSettings?.[index]?.subsequentTimes}
                                helperText={
                                  errors.billingSettings?.[index]?.subsequentTimes?.message
                                }
                                margin="normal"
                                fullWidth
                                InputProps={{
                                  ...params.InputProps,
                                  // endAdornment: null,
                                }}
                                label={t(
                                  'clinicSite.manageSites.siteForm.billingUsageReportSettings.subsequentTimes'
                                )}
                              />
                            )}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                );
              })}
          </RoundedContainer>
        )}

      {currentUser.roleId === Role.SuperAdmin && (
        <>
          {isLoadingCoCMAssessmentType ? (
            <Loading />
          ) : (
            <RoundedContainer sx={{ paddingTop: 1, paddingBottom: 3, marginBottom: 2 }}>
              <Typography
                sx={{ color: 'rgba(0, 0, 0, 0.5)', marginBottom: 1, paddingLeft: 3, paddingTop: 1 }}
              >
                {t('clinicSite.manageSites.siteForm.CoCMSetting')}
              </Typography>

              <Box
                sx={{
                  px: matches ? 0 : 3,
                  pt: 0,
                  mt: 3,
                  mb: 1,
                }}
              >
                <Controller
                  name="optInCoCM"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      {...field}
                      control={<StyledCheckbox />}
                      checked={field.value}
                      onChange={handleOptInCoCM}
                      label={`${t('clinicSite.manageSites.siteForm.optInCoCM')}`}
                    />
                  )}
                />
              </Box>

              <Box
                sx={{
                  px: matches ? 0 : 3,
                  py: 0,
                }}
              >
                <Controller
                  name="defaultCareManagerId"
                  control={control}
                  render={({ fieldState, formState, field: { value }, ...props }) => (
                    <Autocomplete
                      {...props}
                      data-testid="mui-component-select-careManagerId"
                      options={activeCareProviderList || []}
                      getOptionLabel={getDefaultProviderLabel}
                      onChange={handleDefaultCareManagerChange}
                      disabled={!coCMEnabled}
                      value={
                        value
                          ? activeCareProviderList?.find((item) => item.id === value) || null
                          : null
                      }
                      filterOptions={filterDefaultCareManagerOptions}
                      renderOption={(props, option: any) => {
                        return (
                          <li {...props} key={option.id} value={option.id}>
                            {option.firstName && option.lastName
                              ? `${option.lastName}, ${option.firstName}`
                              : ''}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!errors.defaultCareManagerId}
                          helperText={errors.defaultCareManagerId?.message}
                          margin="normal"
                          fullWidth
                          label={t('clinicSite.manageSites.siteForm.defaultCareManager')}
                        />
                      )}
                    />
                  )}
                />
              </Box>

              <Box
                sx={{
                  px: matches ? 0 : 3,
                  pt: 0,
                  mt: 2,
                  mb: 3,
                }}
              >
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: '14px !important' }}
                    dangerouslySetInnerHTML={{
                      __html: t('clinicSite.manageSites.siteForm.CoCMInfo.screeningInfo'),
                    }}
                  ></Typography>
                </Alert>
                <Controller
                  name="screeningAssessments"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      multiple
                      options={Object.keys(coCMAssessmentTypes || {})}
                      disableCloseOnSelect
                      getOptionLabel={(option: string) =>
                        getOptionLabel(option, coCMAssessmentTypes || {})
                      }
                      renderOption={(props, option: string, { selected }) => {
                        const assessment = coCMAssessmentTypes?.[option];
                        if (assessment) {
                          let value = `${assessment?.id}-interview`;
                          if (assessment?.type === ReportType.Monitor) {
                            value = `${assessment?.id}-monitor`;
                          }
                          return (
                            <li key={value} value={value} {...props}>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {assessment.name}
                            </li>
                          );
                        }
                      }}
                      style={{ width: '100%' }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!errors.screeningAssessments}
                          helperText={
                            !!errors.screeningAssessments &&
                            (errors.screeningAssessments as unknown as FieldError)?.message
                          }
                          label={`${t('clinicSite.manageSites.siteForm.screeningAssessments')}`}
                        />
                      )}
                      disabled={!coCMEnabled}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value || []}
                    />
                  )}
                />
              </Box>
              <Box
                sx={{
                  px: matches ? 0 : 3,
                  pt: 0,
                }}
              >
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: '14px !important' }}
                    dangerouslySetInnerHTML={{
                      __html: t('clinicSite.manageSites.siteForm.CoCMInfo.diagnosticInfo'),
                    }}
                  ></Typography>
                </Alert>
                <Controller
                  name="diagnosticAssessments"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      multiple
                      options={Object.keys(coCMAssessmentTypes || {})}
                      disableCloseOnSelect
                      getOptionLabel={(option: string) =>
                        getOptionLabel(option, coCMAssessmentTypes || {})
                      }
                      renderOption={(props, option: string, { selected }) => {
                        const assessment = coCMAssessmentTypes?.[option];
                        if (assessment) {
                          let value = `${assessment?.id}-interview`;
                          if (assessment?.type === 'monitor') {
                            value = `${assessment?.id}-monitor`;
                          }
                          return (
                            <li key={value} value={value} {...props}>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {assessment.name}
                            </li>
                          );
                        }
                      }}
                      style={{ width: '100%' }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!errors.diagnosticAssessments}
                          helperText={
                            !!errors.diagnosticAssessments &&
                            (errors.diagnosticAssessments as unknown as FieldError)?.message
                          }
                          label={`${t('clinicSite.manageSites.siteForm.diagnosticAssessments')}`}
                        />
                      )}
                      disabled={!coCMEnabled}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value || []}
                    />
                  )}
                />
              </Box>
            </RoundedContainer>
          )}
        </>
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          pb: 3,
          px: matches ? 0 : 3,
        }}
      >
        {!matches && <Divider sx={{ width: '100%' }} />}

        {pageErrors.length > 0 &&
          pageErrors.map((error) => (
            <Alert key={error} severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          ))}
        <Button
          onClick={submitForm}
          fullWidth
          variant="contained"
          sx={{
            width: matches ? '100%' : '24%',
            my: 2,
            py: 2,
            float: 'right',
          }}
        >
          {siteId
            ? t('clinicSite.manageSites.siteForm.updateSite')
            : t('clinicSite.manageSites.siteForm.createSite')}
        </Button>
      </Box>
    </Box>
  );
}
